import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, BarChart3, Bot, CheckCircle2, Clock3, Filter,
  Menu, Search, ShieldCheck, SlidersHorizontal, TrendingDown,
  TrendingUp, X
} from 'lucide-react';
import { apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

const NAV_LINKS = [
  ['/explore', 'Explore'],
  ['/features', 'Features'],
  ['/how-it-works', 'How It Works'],
  ['/pricing', 'Pricing'],
  ['/ea-store', 'EA Store'],
];

const money = (value, currency = 'USD') => {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0,
    }).format(amount);
  } catch (_) {
    return `$${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
};

const relativeTime = (value) => {
  if (!value) return 'Sync time unavailable';
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return 'Sync time unavailable';
  const minutes = Math.max(0, Math.floor((Date.now() - time) / 60000));
  if (minutes < 1) return 'Synced just now';
  if (minutes < 60) return `Synced ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Synced ${hours}h ago`;
  return `Synced ${Math.floor(hours / 24)}d ago`;
};

const statusTone = (status) => {
  if (status === 'EXCELLENT') return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300';
  if (status === 'GOOD') return 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300';
  if (status === 'POOR') return 'border-rose-500/30 bg-rose-500/10 text-rose-300';
  return 'border-amber-500/30 bg-amber-500/10 text-amber-300';
};

export default function ExplorePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [accountType, setAccountType] = useState('all');
  const [sort, setSort] = useState('recent');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet(API_ROUTES.public.explore, { auth: false })
      .then((data) => {
        if (!cancelled) setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (!cancelled) setError('Verified profiles could not be loaded. Please try again shortly.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = items.filter((item) => {
      if (accountType !== 'all' && item.account_type !== accountType) return false;
      if (!query) return true;
      return [item.ea_name, item.broker, item.platform, item.status]
        .some((value) => String(value || '').toLowerCase().includes(query));
    });

    return [...filtered].sort((a, b) => {
      if (sort === 'profit') return Number(b.net_profit || 0) - Number(a.net_profit || 0);
      if (sort === 'drawdown') return Number(a.max_drawdown_pct || 0) - Number(b.max_drawdown_pct || 0);
      if (sort === 'trades') return Number(b.total_trades || 0) - Number(a.total_trades || 0);
      return new Date(b.shared_at || 0).getTime() - new Date(a.shared_at || 0).getTime();
    });
  }, [accountType, items, search, sort]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#080d12] text-slate-100">
      <nav className="sticky top-0 z-50 border-b border-[#202c37] bg-[#080d12]/95 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5" aria-label="TrackMyFxEA home">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-400 text-[#07110d]"><BarChart3 size={19} /></div>
            <div><p className="text-sm font-bold text-white">TrackMyFxEA</p><p className="text-[9px] text-slate-600">Verified EA intelligence</p></div>
          </Link>
          <div className="hidden items-center gap-6 md:flex">{NAV_LINKS.map(([to, label]) => <Link key={to} to={to} className={`text-xs font-medium transition-colors ${to === '/explore' ? 'text-emerald-300' : 'text-slate-400 hover:text-white'}`}>{label}</Link>)}</div>
          <div className="hidden items-center gap-2 md:flex"><Link to="/login" className="px-3 py-2 text-xs font-semibold text-slate-300 hover:text-white">Sign in</Link><Link to="/register" className="rounded-md bg-emerald-400 px-4 py-2.5 text-xs font-bold text-[#07110d] hover:bg-emerald-300">Connect account</Link></div>
          <button type="button" onClick={() => setMobileMenuOpen((open) => !open)} className="flex h-9 w-9 items-center justify-center rounded-md border border-[#293643] text-slate-300 md:hidden" aria-label="Toggle navigation">{mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}</button>
        </div>
        {mobileMenuOpen ? <div className="border-t border-[#202c37] bg-[#0a1016] px-4 py-4 md:hidden">{NAV_LINKS.map(([to, label]) => <Link key={to} to={to} onClick={() => setMobileMenuOpen(false)} className="block border-b border-[#202c37] py-3 text-sm text-slate-300">{label}</Link>)}<div className="mt-4 grid grid-cols-2 gap-2"><Link to="/login" className="rounded-md border border-[#293643] px-4 py-2.5 text-center text-xs font-semibold text-slate-200">Sign in</Link><Link to="/register" className="rounded-md bg-emerald-400 px-4 py-2.5 text-center text-xs font-bold text-[#07110d]">Connect</Link></div></div> : null}
      </nav>

      <main>
        <section className="border-b border-[#18232d] px-4 py-14 sm:px-6 sm:py-20">
          <div className="mx-auto grid max-w-6xl gap-9 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-end">
            <div className="max-w-3xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-300"><ShieldCheck size={12} /> Owner-published · Connector verified</div>
              <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">Explore verified EA performance</h1>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-400 sm:text-base">Compare public MT4 and MT5 Expert Advisor profiles using performance data received from the owner's TrackMyFxEA connector—not screenshots or manually entered results.</p>
            </div>
            <div className="rounded-xl border border-[#293643] bg-[#111820] p-5">
              <p className="text-[10px] font-bold uppercase text-cyan-300">What “verified” means</p>
              <p className="mt-2 text-xs leading-5 text-slate-400">The profile metrics come from synced account data. It does not guarantee ownership, profitability, or future results.</p>
            </div>
          </div>
        </section>

        <section className="px-4 py-10 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-3 rounded-xl border border-[#263441] bg-[#0d141b] p-3 md:grid-cols-[minmax(0,1fr)_auto_auto]">
              <label className="relative block">
                <span className="sr-only">Search verified EAs</span>
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search EA, broker, platform..." className="h-11 w-full rounded-md border border-[#293643] bg-[#080d12] pl-10 pr-3 text-sm text-slate-200 outline-none placeholder:text-slate-700 focus:border-emerald-500/50" />
              </label>
              <div className="flex rounded-md border border-[#293643] bg-[#080d12] p-1" aria-label="Account type filter">
                {['all', 'real', 'demo'].map((value) => <button key={value} type="button" onClick={() => setAccountType(value)} className={`rounded px-3 py-2 text-[10px] font-bold uppercase ${accountType === value ? 'bg-emerald-400 text-[#07110d]' : 'text-slate-500 hover:text-slate-200'}`}>{value}</button>)}
              </div>
              <label className="relative block">
                <span className="sr-only">Sort profiles</span>
                <SlidersHorizontal size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                <select value={sort} onChange={(event) => setSort(event.target.value)} className="h-11 min-w-[160px] appearance-none rounded-md border border-[#293643] bg-[#080d12] pl-9 pr-8 text-xs text-slate-300 outline-none">
                  <option value="recent">Recently published</option>
                  <option value="profit">Highest net P/L</option>
                  <option value="drawdown">Lowest drawdown</option>
                  <option value="trades">Most trades</option>
                </select>
              </label>
            </div>

            <div className="mt-5 flex items-center justify-between text-xs text-slate-600"><span>{loading ? 'Loading profiles…' : `${visibleItems.length} verified profile${visibleItems.length === 1 ? '' : 's'}`}</span><span className="hidden items-center gap-1.5 sm:flex"><Filter size={12} /> Public profiles are opt-in</span></div>

            {error ? <div className="mt-6 rounded-xl border border-rose-500/20 bg-rose-500/5 p-8 text-center text-sm text-rose-300">{error}</div> : null}

            {!loading && !error && visibleItems.length === 0 ? (
              <div className="mt-6 rounded-xl border border-[#263441] bg-[#0d141b] px-5 py-14 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-300"><Bot size={22} /></div>
                <h2 className="mt-4 text-lg font-bold text-white">No matching public profiles yet</h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">Account owners can publish an individual EA profile from EA Performance → Detailed Analysis → Publish profile.</p>
                <Link to="/register" className="mt-6 inline-flex items-center gap-2 rounded-md bg-emerald-400 px-5 py-3 text-sm font-bold text-[#07110d]">Track and publish your EA <ArrowRight size={15} /></Link>
              </div>
            ) : null}

            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {visibleItems.map((item) => <ProfileCard key={item.share_token} item={item} />)}
            </div>
          </div>
        </section>

        <section className="border-t border-[#18232d] bg-[#0b1117] px-4 py-14 sm:px-6">
          <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-3">
            {[
              [ShieldCheck, 'Connector verified', 'Metrics are generated from synced MT4/MT5 account data.'],
              [CheckCircle2, 'Privacy-safe by default', 'Private share links stay unlisted until the owner explicitly publishes one account profile.'],
              [Clock3, 'Freshness is visible', 'Every profile shows when its underlying EA statistics last updated.'],
            ].map(([Icon, title, body]) => <div key={title} className="border-l border-[#263441] pl-5"><Icon size={18} className="text-emerald-300" /><h2 className="mt-3 text-sm font-bold text-white">{title}</h2><p className="mt-2 text-xs leading-5 text-slate-500">{body}</p></div>)}
          </div>
        </section>
      </main>
    </div>
  );
}

function ProfileCard({ item }) {
  const profitable = Number(item.net_profit || 0) >= 0;
  return (
    <Link to={item.profile_url || `/shared/${item.share_token}`} className="group rounded-xl border border-[#263441] bg-[#111820] p-5 transition hover:-translate-y-0.5 hover:border-emerald-500/40 hover:shadow-[0_18px_50px_rgba(0,0,0,0.28)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0"><p className="truncate text-base font-bold text-white">{item.ea_name}</p><p className="mt-1 truncate text-[10px] text-slate-600">{item.platform} · {item.broker} · Magic {item.magic}</p></div>
        <span className={`shrink-0 rounded border px-2 py-1 text-[9px] font-bold uppercase ${item.account_type === 'real' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-sky-500/30 bg-sky-500/10 text-sky-300'}`}>{item.account_type}</span>
      </div>
      <div className="mt-4 flex items-center gap-2"><span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-1 text-[9px] font-bold text-emerald-300"><ShieldCheck size={10} /> {item.verification_label || 'Connector verified'}</span><span className={`rounded border px-2 py-1 text-[9px] font-bold ${statusTone(item.status)}`}>{item.status}</span></div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <Metric label="Net P/L" value={money(item.net_profit, item.currency)} tone={profitable ? 'text-emerald-300' : 'text-rose-300'} icon={profitable ? TrendingUp : TrendingDown} />
        <Metric label="Max DD" value={`${Number(item.max_drawdown_pct || 0).toFixed(1)}%`} tone="text-rose-300" />
        <Metric label="Win rate" value={`${Number(item.win_rate || 0).toFixed(1)}%`} tone="text-cyan-300" />
        <Metric label="Profit factor" value={Number(item.profit_factor || 0).toFixed(2)} tone="text-violet-300" />
      </div>
      <div className="mt-5 flex items-center justify-between border-t border-[#263441] pt-4 text-[10px] text-slate-600"><span>{Number(item.total_trades || 0).toLocaleString()} trades · {item.days_active || 0} active days</span><span>{relativeTime(item.last_updated)}</span></div>
    </Link>
  );
}

function Metric({ label, value, tone, icon: Icon }) {
  return <div className="rounded-lg border border-[#263441] bg-[#0b1117] p-3"><p className="text-[9px] font-bold uppercase text-slate-600">{label}</p><p className={`mt-1 flex items-center gap-1.5 text-sm font-bold ${tone}`}>{Icon ? <Icon size={13} /> : null}{value}</p></div>;
}